import { useState } from 'react';
import { Plus, Edit2, Trash2, BookOpen, Users, Calendar, TrendingUp, Layers } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Modal from '../../components/common/Modal';
import { formatCurrency, formatDate, generateId } from '../../utils/helpers';
import DateInput from '../../components/common/DateInput';

const TERM_LABELS = ['Term 1', 'Term 2', 'Term 3'];

function blankTerms() {
  return TERM_LABELS.map((label, i) => ({ termNo: i + 1, label, termPercent: '', termDueDate: '', amount: 0 }));
}

function FeeStructure() {
  const { feeStructures, setFeeStructures, classes, students, feePayments,
          saveFeeStructure, updateFeeStructure, academicYear } = useApp();

  const blankForm = () => ({
    name: '', classId: '', academicYear: academicYear || '', dueDate: '', frequency: 'annually',
    tuitionFee: '',
    splitTerms: false,
    terms: blankTerms(),
    otherComponents: [],
  });

  const [filterClass, setFilterClass] = useState('all');
  const [filterYear,  setFilterYear]  = useState('all');
  const [modal,    setModal]    = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState(blankForm());

  // ── helpers ──────────────────────────────────────────────────────
  const recalcTermAmounts = (terms, tuitionFee) => {
    const total = Number(tuitionFee) || 0;
    return terms.map(t => ({ ...t, amount: t.termPercent ? Math.round(total * Number(t.termPercent) / 100) : 0 }));
  };

  const totalTermPct = form.terms.reduce((s, t) => s + Number(t.termPercent || 0), 0);
  const pctBalanced  = totalTermPct === 100;

  const updateTuition = (val) => {
    setForm(f => ({ ...f, tuitionFee: val, terms: recalcTermAmounts(f.terms, val) }));
  };

  const updateTerm = (i, field, val) => {
    setForm(f => {
      const terms = f.terms.map((t, idx) => {
        if (idx !== i) return t;
        const updated = { ...t, [field]: val };
        if (field === 'termPercent') {
          updated.amount = Math.round(Number(f.tuitionFee || 0) * Number(val) / 100);
        }
        return updated;
      });
      return { ...f, terms };
    });
  };

  const addOther    = () => setForm(f => ({ ...f, otherComponents: [...f.otherComponents, { name: '', amount: '' }] }));
  const removeOther = (i) => setForm(f => ({ ...f, otherComponents: f.otherComponents.filter((_, idx) => idx !== i) }));
  const updateOther = (i, field, val) => {
    setForm(f => ({ ...f, otherComponents: f.otherComponents.map((c, idx) => idx === i ? { ...c, [field]: val } : c) }));
  };

  const grandTotal = () => {
    const tuition = Number(form.tuitionFee) || 0;
    const others  = form.otherComponents.reduce((s, c) => s + Number(c.amount || 0), 0);
    return tuition + others;
  };

  // ── open modal ───────────────────────────────────────────────────
  const openAdd = () => { setForm(blankForm()); setEditing(null); setModal(true); };

  const openEdit = (fs) => {
    const termComps  = (fs.components || []).filter(c => c.isTermFee);
    const otherComps = (fs.components || []).filter(c => !c.isTermFee);
    const tuitionFee = termComps.length > 0
      ? termComps.reduce((s, c) => s + Number(c.amount || 0), 0)
      : (otherComps.find(c => c.name === 'Tuition Fee')?.amount || fs.totalAmount || '');
    const isSplit = termComps.length > 0;

    // build terms array, filling from saved data
    const terms = TERM_LABELS.map((label, i) => {
      const saved = termComps.find(t => t.termNo === i + 1);
      return saved
        ? { termNo: i + 1, label, termPercent: saved.termPercent || '', termDueDate: saved.termDueDate || '', amount: saved.amount || 0 }
        : { termNo: i + 1, label, termPercent: '', termDueDate: '', amount: 0 };
    });

    const others = isSplit
      ? otherComps
      : otherComps.filter(c => c.name !== 'Tuition Fee');

    setForm({
      name: fs.name || '', classId: fs.classId || '',
      academicYear: fs.academicYear || '2025-2026',
      dueDate: fs.dueDate || '', frequency: fs.frequency || 'annually',
      tuitionFee: String(tuitionFee || ''),
      splitTerms: isSplit,
      terms,
      otherComponents: others,
    });
    setEditing(fs);
    setModal(true);
  };

  const remove = (id) => {
    if (window.confirm('Delete fee structure?'))
      setFeeStructures(prev => prev.filter(f => f.id !== id));
  };

  // ── save ─────────────────────────────────────────────────────────
  const save = async () => {
    const tuition = Number(form.tuitionFee) || 0;
    const components = [];

    if (form.splitTerms) {
      form.terms.forEach(t => {
        components.push({
          name: t.label, amount: t.amount, isTermFee: true,
          termNo: t.termNo, termPercent: Number(t.termPercent) || 0,
          termDueDate: t.termDueDate || null,
        });
      });
    } else if (tuition > 0) {
      components.push({ name: 'Tuition Fee', amount: tuition, isTermFee: false });
    }

    form.otherComponents.forEach(c => {
      if (c.name.trim()) components.push({ name: c.name, amount: Number(c.amount) || 0, isTermFee: false });
    });

    const totalAmount = grandTotal();
    const data = {
      name: form.name, classId: form.classId,
      academicYear: form.academicYear, dueDate: form.dueDate,
      frequency: form.frequency, components, totalAmount,
    };

    if (editing) await updateFeeStructure(editing.id, { ...data, id: editing.id });
    else         await saveFeeStructure({ ...data, id: generateId('FEE') });
    setModal(false);
  };

  // Unique class names sorted naturally (LKG, UKG, Class 1 … Class 10)
  const classNames = [...new Set(classes.map(c => c.name))].sort((a, b) => {
    const num = s => { const m = s.match(/\d+/); return m ? Number(m[0]) : -1; };
    return num(a) - num(b) || a.localeCompare(b);
  });

  // ── display helpers ───────────────────────────────────────────────
  // classId now stores the class NAME (e.g. "Class 1"), not a DB row id
  const getClass = (classId) => classId || '-';

  const getStats = (fs) => {
    // Match all students whose class name equals fs.classId
    const enrolled  = students.filter(s => classes.find(c => c.id === s.classId)?.name === fs.classId).length;
    const payments  = feePayments.filter(p => p.feeStructureId === fs.id);
    const collected = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
    const expected  = enrolled * (fs.totalAmount || 0);
    const pct = expected > 0 ? Math.round((collected / expected) * 100) : 0;
    return { enrolled, collected, expected, pct };
  };

  const academicYears = [...new Set(feeStructures.map(f => f.academicYear))].sort().reverse();
  const filtered = feeStructures.filter(fs =>
    (filterClass === 'all' || fs.classId === filterClass) &&
    (filterYear  === 'all' || fs.academicYear === filterYear)
  );
  const filterClassNames = [...new Set(feeStructures.map(f => f.classId))].filter(Boolean).sort();

  const freqColor = {
    monthly:   'bg-blue-100 text-blue-700',
    quarterly: 'bg-purple-100 text-purple-700',
    annually:  'bg-green-100 text-green-700',
  };
  const inp = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300';

  // ── term label colors ─────────────────────────────────────────────
  const termColor = ['bg-blue-100 text-blue-700', 'bg-purple-100 text-purple-700', 'bg-green-100 text-green-700'];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Fee Structure</h1>
          <p className="text-gray-500 text-sm mt-0.5">{filtered.length} structure{filtered.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={filterYear} onChange={e => setFilterYear(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none">
            <option value="all">All Years</option>
            {academicYears.map(y => <option key={y}>{y}</option>)}
          </select>
          <select value={filterClass} onChange={e => setFilterClass(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none">
            <option value="all">All Classes</option>
            {filterClassNames.map(name => <option key={name} value={name}>{name}</option>)}
          </select>
          <button onClick={openAdd}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium">
            <Plus size={16} />Add Structure
          </button>
        </div>
      </div>

      {/* Cards */}
      <div className="grid gap-3">
        {filtered.length === 0 && (
          <div className="text-center py-10">
            <BookOpen size={36} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm text-gray-500">No fee structures found</p>
          </div>
        )}
        {filtered.map(fs => {
          const stats    = getStats(fs);
          const total    = fs.totalAmount || 0;
          const termComps  = (fs.components || []).filter(c => c.isTermFee);
          const otherComps = (fs.components || []).filter(c => !c.isTermFee);
          return (
            <div key={fs.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="flex items-start justify-between px-4 py-3 border-b border-gray-100">
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <h3 className="font-semibold">{fs.name}</h3>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${freqColor[fs.frequency] || 'bg-gray-100'}`}>{fs.frequency}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><BookOpen size={12} />{getClass(fs.classId)}</span>
                    <span>{fs.academicYear}</span>
                    {fs.dueDate && <span className="flex items-center gap-1"><Calendar size={12} />Due: {formatDate(fs.dueDate)}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(fs)} className="p-1.5 hover:bg-indigo-50 rounded-lg text-indigo-600"><Edit2 size={14} /></button>
                  <button onClick={() => remove(fs.id)} className="p-1.5 hover:bg-red-50 rounded-lg text-red-500"><Trash2 size={14} /></button>
                </div>
              </div>

              <div className="px-4 py-3 grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Fee breakdown */}
                <div className="lg:col-span-2">
                  {termComps.length > 0 ? (
                    <div>
                      <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1"><Layers size={12} />Tuition — Term Schedule</p>
                      <div className="flex gap-2 flex-wrap">
                        {termComps.map((t, i) => (
                          <div key={i} className="flex-1 min-w-[120px] bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                            <p className={`text-xs font-semibold mb-1 inline-block px-1.5 py-0.5 rounded ${termColor[i] || 'bg-gray-100'}`}>{t.name}</p>
                            <p className="font-bold text-gray-800">{formatCurrency(t.amount)}</p>
                            <p className="text-xs text-gray-400">{t.termPercent}% {t.termDueDate ? `· Due ${formatDate(t.termDueDate)}` : ''}</p>
                          </div>
                        ))}
                      </div>
                      {otherComps.length > 0 && (
                        <div className="mt-2 space-y-1">
                          {otherComps.map((c, i) => (
                            <div key={i} className="flex justify-between text-sm text-gray-600">
                              <span>{c.name}</span>
                              <span className="font-medium">{formatCurrency(c.amount)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {otherComps.length > 0 ? otherComps.map((c, i) => (
                        <div key={i} className="flex justify-between text-sm text-gray-600 border-b border-gray-50 pb-1">
                          <span>{c.name}</span>
                          <span className="font-medium">{formatCurrency(c.amount)}</span>
                        </div>
                      )) : total > 0 ? (
                        <div className="flex justify-between text-sm text-gray-600 border-b border-gray-50 pb-1">
                          <span>Tuition Fee</span>
                          <span className="font-medium">{formatCurrency(total)}</span>
                        </div>
                      ) : null}
                    </div>
                  )}
                  <div className="flex justify-between mt-2 pt-1 border-t border-gray-200">
                    <span className="text-sm font-semibold">Total</span>
                    <span className="font-bold text-indigo-600">{formatCurrency(total)}</span>
                  </div>
                </div>

                {/* Collection stats */}
                <div className="border-t lg:border-t-0 lg:border-l border-gray-100 pt-3 lg:pt-0 lg:pl-4">
                  <p className="text-xs font-medium text-gray-500 mb-2">Collection Status</p>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="flex items-center gap-1 text-gray-600"><Users size={12} />Enrolled</span>
                      <span className="font-semibold">{stats.enrolled} students</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Expected</span>
                      <span className="font-semibold">{formatCurrency(stats.expected)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="flex items-center gap-1 text-gray-600"><TrendingUp size={12} />Collected</span>
                      <span className="font-semibold text-green-600">{formatCurrency(stats.collected)}</span>
                    </div>
                    <div>
                      <div className="flex justify-between mb-1 text-xs text-gray-400">
                        <span>Collection rate</span><span>{stats.pct}%</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div className={`h-1.5 rounded-full ${stats.pct >= 80 ? 'bg-green-500' : stats.pct >= 50 ? 'bg-yellow-400' : 'bg-red-400'}`}
                          style={{ width: `${Math.min(stats.pct, 100)}%` }} />
                      </div>
                    </div>
                    {stats.expected > stats.collected && (
                      <div className="bg-orange-50 rounded px-2 py-1 text-xs text-orange-600 font-medium">
                        Pending: {formatCurrency(stats.expected - stats.collected)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Modal ── */}
      <Modal isOpen={modal} onClose={() => setModal(false)}
        title={editing ? `Edit Fee Structure — ${getClass(form.classId)}` : 'Add Fee Structure'}
        size="lg">
        <div className="space-y-4">

          {/* Class */}
          <div>
            <label className="block text-sm font-medium mb-1">Class <span className="text-gray-400 font-normal">(applies to all sections)</span></label>
            <select value={form.classId || ''} onChange={e => setForm(f => ({ ...f, classId: e.target.value }))} className={inp}>
              <option value="">Select Class</option>
              {classNames.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium mb-1">Structure Name</label>
            <input value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className={inp} placeholder="e.g. LKG (2026-27)" />
          </div>

          {/* Year + Frequency */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Academic Year</label>
              <input value={form.academicYear || ''} onChange={e => setForm(f => ({ ...f, academicYear: e.target.value }))}
                className={inp} placeholder="e.g. 2025-2026" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Frequency</label>
              <select value={form.frequency || 'annually'} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))} className={inp}>
                {['monthly', 'quarterly', 'annually'].map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
          </div>

          {/* Due date */}
          <div>
            <label className="block text-sm font-medium mb-1">Default Due Date</label>
            <DateInput value={form.dueDate || ''} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} className={inp} />
          </div>

          {/* ── Tuition Fee card ── */}
          <div className="border border-indigo-200 rounded-xl overflow-hidden">
            {/* Card header */}
            <div className="flex items-center justify-between px-4 py-3 bg-indigo-50">
              <div className="flex items-center gap-2">
                <Layers size={16} className="text-indigo-600" />
                <span className="text-sm font-semibold text-indigo-700">Tuition Fee</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-indigo-500">Split into 3 Terms</span>
                <button
                  onClick={() => setForm(f => ({ ...f, splitTerms: !f.splitTerms }))}
                  className={`relative w-11 h-6 rounded-full transition-colors ${form.splitTerms ? 'bg-indigo-600' : 'bg-gray-300'}`}>
                  <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.splitTerms ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
            </div>

            <div className="px-4 py-3 space-y-3">
              {/* Total tuition */}
              <div className="flex items-center gap-3">
                <label className="text-sm font-semibold text-indigo-700 w-32 shrink-0">Total Tuition</label>
                <input type="number" value={form.tuitionFee || ''} onChange={e => updateTuition(e.target.value)}
                  className="flex-1 px-3 py-2 border-2 border-indigo-300 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  placeholder="Enter total tuition amount" />
              </div>

              {/* Term split rows */}
              {form.splitTerms && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">Enter % share for each term.</p>
                  <div className="space-y-2">
                    {form.terms.map((t, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-lg w-16 text-center shrink-0 ${termColor[i]}`}>{t.label}</span>
                        <div className="relative w-24 shrink-0">
                          <input type="number" min="0" max="100" value={t.termPercent || ''}
                            onChange={e => updateTerm(i, 'termPercent', e.target.value)}
                            className="w-full pl-3 pr-7 py-2 border border-gray-300 rounded-lg text-sm text-center focus:outline-none focus:ring-1 focus:ring-indigo-300"
                            placeholder="0" />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500 pointer-events-none">%</span>
                        </div>
                        <div className="w-32 shrink-0 px-3 py-2 bg-indigo-50 rounded-lg text-sm font-semibold text-indigo-700 text-right">
                          {t.amount ? formatCurrency(t.amount) : <span className="text-gray-400">—</span>}
                        </div>
                        <DateInput value={t.termDueDate || ''}
                          onChange={e => updateTerm(i, 'termDueDate', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-indigo-300" />
                      </div>
                    ))}
                  </div>
                  {/* Balance indicator */}
                  <div className={`mt-2 flex justify-between text-xs font-semibold px-2 py-1.5 rounded-lg ${pctBalanced ? 'bg-green-50 text-green-700' : 'bg-orange-50 text-orange-600'}`}>
                    <span>Total: {totalTermPct}%</span>
                    <span>{pctBalanced ? '✓ Balanced' : `${100 - totalTermPct}% remaining`}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Other Fee Components ── */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-gray-700">Other Fee Components</label>
              <button onClick={addOther} className="text-indigo-600 hover:text-indigo-800 text-sm font-medium">+ Add Component</button>
            </div>
            {form.otherComponents.length === 0 ? (
              <p className="text-xs text-gray-400 italic">No additional components. Click + Add Component to add books, lab fee, etc.</p>
            ) : (
              <div className="space-y-2">
                {form.otherComponents.map((c, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <input placeholder="e.g. Books, Lab Fee" value={c.name || ''}
                      onChange={e => updateOther(i, 'name', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
                    <input type="number" placeholder="Amount ₹" value={c.amount || ''}
                      onChange={e => updateOther(i, 'amount', e.target.value)}
                      className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
                    <button onClick={() => removeOther(i)} className="text-red-400 hover:text-red-600 p-1">✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Grand total */}
          <div className="flex justify-between items-center bg-indigo-50 rounded-lg px-4 py-2.5">
            <span className="text-sm font-semibold text-indigo-700">Total Amount</span>
            <span className="text-base font-bold text-indigo-700">{formatCurrency(grandTotal())}</span>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <button onClick={() => setModal(false)} className="px-4 py-2 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={save} className="px-5 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium">Save</button>
        </div>
      </Modal>
    </div>
  );
}

export default FeeStructure;
